import React, { useState, useEffect } from "react";
import axios from "axios";
import DOMPurify from "dompurify";
import { Activity as ActivityIcon, ChevronDown, CornerUpLeft, Eye, Tag, Mail, Lock, Bookmark, Phone } from "lucide-react";

const SOURCING_URL = process.env.REACT_APP_SOURCING_API_URL;

function formatDateTime(d) {
    if (!d) return "—";
    const dt = new Date(d);
    const date = dt.toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" });
    const time = dt.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" });
    return `${date} at ${time}`;
}

const STATUS_LABEL = {
    active: "In progress",
    completed: "Finished (No Reply)",
    replied: "Replied",
    cancelled: "Cancelled",
    paused: "Paused",
};
const statusLabel = (s) => STATUS_LABEL[s] ?? s;

// Per-email status dot + label for the email cards.
const emailDot = (status) => {
    switch (status) {
        case "sent": return { text: "Sent", color: "#7c3aed" };
        case "failed": return { text: "Failed", color: "#ef4444" };
        case "skipped": return { text: "Skipped", color: "#9ca3af" };
        default: return { text: "Pending", color: "#f59e0b" };
    }
};

// Per-call status dot + label for the call cards. Mirrors CallStatus (pending/triggered/
// failed/skipped) — "triggered" only means handed off to the calling service, not answered.
const callDot = (status) => {
    switch (status) {
        case "triggered": return { text: "Call placed", color: "#7c3aed" };
        case "failed": return { text: "Failed", color: "#ef4444" };
        case "skipped": return { text: "Skipped", color: "#9ca3af" };
        default: return { text: "Scheduled", color: "#f59e0b" };
    }
};

const INTENT_LABEL = { interested: "Interested", not_interested: "Not interested" };

// Interest badge for the call's extracted intent (from the full call outcome, not the
// screening-form submission's coarser intent field above). Classes rather than inline
// colours so the badge follows the theme; 600-on-100 is the same pair the hexes were.
const INTEREST_STYLE = {
    interested: { label: "Interested", className: "bg-green-100 text-green-600 dark:bg-green-500/15 dark:text-green-300" },
    not_interested: { label: "Not interested", className: "bg-red-100 text-red-600 dark:bg-red-500/15 dark:text-red-300" },
    maybe: { label: "Maybe", className: "bg-amber-100 text-amber-600 dark:bg-amber-500/15 dark:text-amber-300" },
};

const INTENT_FIELDS = [
    ["notice_period", "Notice period"],
    ["expected_ctc", "Expected CTC"],
    ["current_status", "Current status"],
];

function timeAgo(dateStr) {
    if (!dateStr) return "";
    const diff = Date.now() - new Date(dateStr).getTime();
    const days = Math.floor(diff / 86400000);
    if (days <= 0) return "today";
    if (days < 30) return `${days}d ago`;
    const months = Math.floor(days / 30);
    if (months < 12) return `${months}mo ago`;
    return `${Math.floor(months / 12)}y ago`;
}

const ACTION_LABEL = {
    viewed: "Viewed profile",
    shortlisted: "Shortlisted",
    saved: "Saved",
    added_to_sequence: "Added to an outreach",
    status_changed: "Changed status",
    email_revealed: "Revealed email",
};
const actionLabel = (a, meta) =>
    a === "status_changed" && meta?.new_status_label ? `Moved to ${meta.new_status_label}`
        : ACTION_LABEL[a] ?? (a || "").replace(/_/g, " ");

// Club consecutive identical activities (same action + recruiter, same day) into
// one entry with a count — so repeated "Viewed profile" events don't flood the list.
function groupActivities(activities) {
    const groups = [];
    for (const act of activities) {
        const last = groups[groups.length - 1];
        const sameDay = last && new Date(last.created_at).toDateString() === new Date(act.created_at).toDateString();
        const sameKind = last
            && last.action === act.action
            && (last.metadata?.recruiter_name || "") === (act.metadata?.recruiter_name || "")
            && (last.metadata?.new_status_label || "") === (act.metadata?.new_status_label || "");
        if (sameKind && sameDay) {
            last.count += 1;
        } else {
            groups.push({ ...act, count: 1 });
        }
    }
    return groups;
}

// A distinct icon per action.
const ACTIVITY_ICON = {
    viewed: Eye,
    status_changed: Tag,
    added_to_sequence: Mail,
    email_revealed: Lock,
    shortlisted: Bookmark,
    saved: Bookmark,
};
const activityIcon = (action) => ACTIVITY_ICON[action] || ActivityIcon;

// Natural-language activity sentence (recruiter name emphasised).
const ActivityText = ({ act }) => {
    const m = act.metadata || {};
    // Lowercase fallback for mid-sentence ("…by a recruiter"), capitalized for sentence start.
    const who = <span className="font-medium text-gray-900 dark:text-white">{m.recruiter_name || "a recruiter"}</span>;
    const Who = <span className="font-medium text-gray-900 dark:text-white">{m.recruiter_name || "A recruiter"}</span>;
    switch (act.action) {
        case "viewed":
            return <>Viewed by {who}</>;
        case "status_changed":
            return <>{Who} updated the status of this profile to &quot;{m.new_status_label || m.new_status || "—"}&quot;</>;
        case "added_to_sequence":
            return <>{Who} added this profile to outreach &quot;{m.sequence_name || "an outreach"}&quot;</>;
        case "email_revealed":
            return <>Contact info unlocked by {who}</>;
        case "shortlisted":
            return <>Shortlisted by {who}</>;
        case "saved":
            return <>Saved by {who}</>;
        default:
            return <>{actionLabel(act.action, act.metadata)} by {who}</>;
    }
};

/**
 * Right-hand pane shown when the candidate panel is expanded.
 * Tabs: Activity (default) + Sequences. Renders only the pane — the candidate
 * profile on the left is the panel's own unchanged UI.
 */
const CandidateDetailTabs = ({ candidate }) => {
    const orgId = localStorage.getItem("current_org_id");
    const [tab, setTab] = useState("activity");

    const [activities, setActivities] = useState([]);
    const [activitiesLoading, setActivitiesLoading] = useState(false);

    const [seqLoading, setSeqLoading] = useState(false);
    const [enrollment, setEnrollment] = useState(null);
    const [sequenceName, setSequenceName] = useState(null);
    const [emails, setEmails] = useState([]);
    const [calls, setCalls] = useState([]);
    const [expandedEmails, setExpandedEmails] = useState(new Set());
    const [expandedCalls, setExpandedCalls] = useState(new Set());
    const [callDetails, setCallDetails] = useState({}); // { [callId]: { loading, data } }
    const [callSubTab, setCallSubTab] = useState({}); // { [callId]: "intent" | "transcript" }

    const recruiterName = localStorage.getItem("username") || "Recruiter";
    const firstName = candidate.first_name || (candidate.full_name || "").split(" ")[0] || "candidate";

    const toggleEmail = (id) =>
        setExpandedEmails(prev => {
            const next = new Set(prev);
            next.has(id) ? next.delete(id) : next.add(id);
            return next;
        });

    // Details/transcript live in the Calling microservice, not Sourcing's own DB — fetched
    // lazily on first expand and cached per call id so re-toggling doesn't re-fetch.
    const toggleCall = (call) => {
        setExpandedCalls(prev => {
            const next = new Set(prev);
            next.has(call.id) ? next.delete(call.id) : next.add(call.id);
            return next;
        });
        if (!callDetails[call.id]) {
            setCallDetails(prev => ({ ...prev, [call.id]: { loading: true, data: null } }));
            axios.get(`${SOURCING_URL}/calls/${call.id}/details`)
                .then(({ data }) => setCallDetails(prev => ({ ...prev, [call.id]: { loading: false, data } })))
                .catch(() => setCallDetails(prev => ({ ...prev, [call.id]: { loading: false, data: { found: false } } })));
        }
    };

    const markReplied = async () => {
        if (!enrollment) return;
        try {
            await axios.patch(`${SOURCING_URL}/email/enrollments/${enrollment.id}/mark-replied`);
            setEnrollment(prev => ({ ...prev, status: "replied" }));
        } catch { /* ignore */ }
    };

    useEffect(() => {
        if (tab !== "activity" || !candidate?.candidate_id) return;
        setActivitiesLoading(true);
        axios.get(`${SOURCING_URL}/sourcing/activity`, {
            params: { candidate_id: candidate.candidate_id, organisation_id: orgId },
        })
            .then(({ data }) => setActivities(data.activities || []))
            .catch(() => setActivities([]))
            .finally(() => setActivitiesLoading(false));
    }, [tab, candidate?.candidate_id, orgId]);

    useEffect(() => {
        if (tab !== "sequences" || !candidate?.candidate_id) return;
        setSeqLoading(true);
        setEnrollment(null);
        setEmails([]);
        setCalls([]);
        setSequenceName(null);
        axios.get(`${SOURCING_URL}/email/enrollments/by-candidate`, {
            params: { org_id: orgId, candidate_id: candidate.candidate_id },
        })
            .then(async ({ data }) => {
                if (!data.enrolled) return;
                setEnrollment(data.enrollment);
                const [emailsRes, callsRes, seqRes] = await Promise.all([
                    axios.get(`${SOURCING_URL}/email/enrollments/${data.enrollment.id}/emails`),
                    axios.get(`${SOURCING_URL}/calls/by-enrollment/${data.enrollment.id}`).catch(() => ({ data: [] })),
                    axios.get(`${SOURCING_URL}/email/sequences`, { params: { org_id: orgId } }),
                ]);
                const sorted = (emailsRes.data || []).slice().sort((a, b) => new Date(a.scheduled_at) - new Date(b.scheduled_at));
                setEmails(sorted);
                setCalls(callsRes.data || []);
                const seq = (seqRes.data || []).find(s => s.id === data.enrollment.sequence_id);
                setSequenceName(seq?.name || "Outreach");
            })
            .catch(() => {})
            .finally(() => setSeqLoading(false));
    }, [tab, candidate?.candidate_id, orgId]);

    const TABS = [
        { key: "activity", label: "Activity" },
        { key: "sequences", label: "Outreach" },
    ];

    return (
        <div className="flex flex-col h-full min-h-0">
            <div className="px-6 pt-5 pb-4 shrink-0">
                <div className="inline-flex items-center gap-1 bg-gray-100 dark:bg-white/10 rounded-xl p-1">
                    {TABS.map(t => (
                        <button
                            key={t.key}
                            onClick={() => setTab(t.key)}
                            className={`flex items-center gap-1.5 px-4 py-1.5 rounded-lg text-sm font-medium transition-all ${
                                tab === t.key ? "bg-white dark:bg-white/10 text-purple-600 dark:text-purple-300 shadow-sm" : "text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200"
                            }`}
                        >
                         {t.label}
                        </button>
                    ))}
                </div>
            </div>

            <div className="flex-1 overflow-y-auto px-6 py-7">
                {tab === "activity" && (
                    activitiesLoading ? (
                        <div className="flex justify-center py-12"><div className="w-5 h-5 border-2 border-purple-400 border-t-transparent rounded-full animate-spin" /></div>
                    ) : activities.length === 0 ? (
                        <p className="text-sm text-gray-400 dark:text-gray-500 text-center py-12">No activity recorded yet.</p>
                    ) : (
                        (() => {
                            const grouped = groupActivities(activities);
                            return (
                                <div>
                                    <p className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">All activity ({grouped.length})</p>
                                    <div className="flex flex-col gap-3">
                                        {grouped.map((act, i) => {
                                            const Icon = activityIcon(act.action);
                                            return (
                                                <div key={act.id || i} className="flex gap-3 border border-gray-100 dark:border-gray-700/50 rounded-xl px-4 py-3.5">
                                                    <div className="w-8 h-8 rounded-full bg-gray-50 dark:bg-white/[0.03] border border-gray-100 dark:border-gray-700/50 flex items-center justify-center shrink-0">
                                                        <Icon className="w-4 h-4 text-gray-400 dark:text-gray-500" />
                                                    </div>
                                                    <div className="flex-1 min-w-0">
                                                        <div className="flex items-start justify-between gap-2">
                                                            <p className="text-sm text-gray-700 dark:text-gray-300">
                                                                <ActivityText act={act} />
                                                                {act.count > 1 && <span className="text-gray-400 dark:text-gray-500"> · {act.count}×</span>}
                                                            </p>
                                                            <span className="text-xs text-gray-400 dark:text-gray-500 shrink-0 whitespace-nowrap">{timeAgo(act.created_at)}</span>
                                                        </div>
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>
                            );
                        })()
                    )
                )}

                {tab === "sequences" && (
                    seqLoading ? (
                        <div className="flex justify-center py-12"><div className="w-5 h-5 border-2 border-purple-400 border-t-transparent rounded-full animate-spin" /></div>
                    ) : !enrollment ? (
                        <div className="text-center py-12">
                            <p className="text-sm font-semibold text-gray-700 dark:text-gray-300">Not in an outreach</p>
                            <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">This candidate isn't enrolled in any outreach yet.</p>
                        </div>
                    ) : (
                        <div>
                            {/* Detail rows */}
                            <div className="flex flex-col gap-3.5 text-sm mb-6 pb-6">
                                <div className="flex gap-4">
                                    <span className="w-36 shrink-0 text-gray-400 dark:text-gray-500">Outreach name</span>
                                    <span className="flex-1 min-w-0 text-purple-600 dark:text-purple-300 font-medium">{sequenceName}</span>
                                </div>
                                <div className="flex gap-4">
                                    <span className="w-36 shrink-0 text-gray-400 dark:text-gray-500">Current status</span>
                                    <span className="flex-1 min-w-0 text-gray-700 dark:text-gray-300">
                                        {statusLabel(enrollment.status)}
                                        {enrollment.status !== "replied" && enrollment.status !== "cancelled" && (
                                            <button onClick={markReplied} className="text-purple-600 dark:text-purple-300 font-medium hover:underline ml-2">Mark as Replied</button>
                                        )}
                                    </span>
                                </div>
                                <div className="flex gap-4">
                                    <span className="w-36 shrink-0 text-gray-400 dark:text-gray-500">Send emails from</span>
                                    <span className="flex-1 min-w-0 text-gray-700 dark:text-gray-300 truncate">{recruiterName} &lt;{emails[0]?.from_address || "—"}&gt;</span>
                                </div>
                                <div className="flex gap-4">
                                    <span className="w-36 shrink-0 text-gray-400 dark:text-gray-500">Added on</span>
                                    <span className="flex-1 min-w-0 text-gray-700 dark:text-gray-300">{formatDateTime(enrollment.enrolled_at)}</span>
                                </div>
                                <div className="flex gap-4">
                                    <span className="w-36 shrink-0 text-gray-400 dark:text-gray-500">Added by</span>
                                    <span className="flex-1 min-w-0 text-gray-700 dark:text-gray-300">{recruiterName}</span>
                                </div>
                            </div>

                            {/* Reply button. border-btn brings its own padding, pill radius, text
                                size and gradient outline — so no sizing/colour utilities here to
                                fight it. border-btn-dark is its dark-theme counterpart. */}
                            {candidate.email && (
                                <a
                                    href={`mailto:${candidate.email}`}
                                    className="border-btn border-btn-dark !py-2 !px-3 !text-xs inline-flex items-center gap-2 font-normal mb-6"
                                >
                                    <CornerUpLeft className="w-3 h-3" /> Reply to {firstName}
                                </a>
                            )}

                            {/* Email cards */}
                            <div className="flex flex-col gap-5">
                                {emails.map((em) => {
                                    const dot = emailDot(em.status);
                                    const open = expandedEmails.has(em.id);
                                    return (
                                        <div key={em.id} className="border border-gray-100 dark:border-gray-700/50 rounded-xl px-5 py-4 hover:border-gray-200 dark:hover:border-gray-600 transition-all">
                                            <div className="flex items-start gap-3">
                                                <div className="w-9 h-9 rounded-full flex items-center justify-center text-white text-xs font-semibold shrink-0" style={{ background: "linear-gradient(135deg, #a78bfa, #60a5fa)" }}>
                                                    {(recruiterName[0] || "R").toUpperCase()}
                                                </div>
                                                <div className="flex-1 min-w-0">
                                                    <div className="flex items-start justify-between gap-2">
                                                        <p className="text-sm">
                                                            <span className="font-semibold text-gray-900 dark:text-white">{recruiterName}</span>
                                                            <span className="text-gray-500 dark:text-gray-400"> to </span>
                                                            <span className="font-semibold text-gray-900 dark:text-white">{candidate.full_name}</span>
                                                            <span className="text-gray-400 dark:text-gray-500"> &lt;{em.from_address}&gt;</span>
                                                        </p>
                                                        <button onClick={() => toggleEmail(em.id)} className="text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-200 shrink-0">
                                                            <ChevronDown className={`w-4 h-4 transition-transform ${open ? "rotate-180" : ""}`} />
                                                        </button>
                                                    </div>
                                                    <p className="text-sm mt-1 flex items-center gap-1.5">
                                                        <span className="w-1.5 h-1.5 rounded-full shrink-0" style={{ backgroundColor: dot.color }} />
                                                        <span className="font-medium" style={{ color: dot.color }}>{dot.text}</span>
                                                        <span className="text-gray-500 dark:text-gray-400">{formatDateTime(em.sent_at || em.scheduled_at)}</span>
                                                    </p>
                                                    {open && em.subject && (
                                                        <p className="text-sm font-medium text-gray-800 dark:text-white mt-2">{em.subject}</p>
                                                    )}
                                                    {open ? (
                                                        <div className="text-sm text-gray-600 dark:text-gray-300 mt-2" dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(em.body || "") }} />
                                                    ) : (
                                                        <p className="text-sm text-gray-600 dark:text-gray-300 mt-2 truncate">
                                                            {(em.body || "").replace(/<[^>]*>/g, " ").replace(/\s+/g, " ").trim()}
                                                        </p>
                                                    )}
                                                    {open && em.error_message && (
                                                        <p className="text-xs text-red-500 dark:text-red-400 mt-2">Error: {em.error_message}</p>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>

                            {/* Call cards — expand for intent + transcript, fetched live from
                                the calling microservice (Sourcing only tracks that a call was
                                asked for, not what was said). */}
                            {calls.length > 0 && (
                                <div className="flex flex-col gap-5 mt-5">
                                    {calls.map((call) => {
                                        const dot = callDot(call.status);
                                        const open = expandedCalls.has(call.id);
                                        const detail = callDetails[call.id];
                                        const outcome = detail?.data?.found ? detail.data.outreach_outcome : null;
                                        const transcript = detail?.data?.found ? (detail.data.transcript || []) : [];
                                        const subTab = callSubTab[call.id] || "intent";
                                        const interest = outcome && INTEREST_STYLE[outcome.interest_level];
                                        return (
                                            <div key={call.id} className="border border-gray-100 dark:border-gray-700/50 rounded-xl px-5 py-4 hover:border-gray-200 dark:hover:border-gray-600 transition-all">
                                                <div className="flex items-start gap-3">
                                                    <div className="w-9 h-9 rounded-full flex items-center justify-center text-white shrink-0" style={{ background: "linear-gradient(135deg, #34d399, #60a5fa)" }}>
                                                        <Phone className="w-4 h-4" />
                                                    </div>
                                                    <div className="flex-1 min-w-0">
                                                        <div className="flex items-start justify-between gap-2">
                                                            <p className="text-sm">
                                                                <span className="font-semibold text-gray-900 dark:text-white">AI call</span>
                                                                <span className="text-gray-500 dark:text-gray-400"> to </span>
                                                                <span className="font-semibold text-gray-900 dark:text-white">{call.candidate_name}</span>
                                                                <span className="text-gray-400 dark:text-gray-500"> &lt;{call.candidate_phone}&gt;</span>
                                                            </p>
                                                            <button onClick={() => toggleCall(call)} className="text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-200 shrink-0">
                                                                <ChevronDown className={`w-4 h-4 transition-transform ${open ? "rotate-180" : ""}`} />
                                                            </button>
                                                        </div>
                                                        <p className="text-sm mt-1 flex items-center gap-1.5">
                                                            <span className="w-1.5 h-1.5 rounded-full shrink-0" style={{ backgroundColor: dot.color }} />
                                                            <span className="font-medium" style={{ color: dot.color }}>{dot.text}</span>
                                                            <span className="text-gray-500 dark:text-gray-400">{formatDateTime(call.triggered_at || call.scheduled_at)}</span>
                                                        </p>
                                                        {call.intent && (
                                                            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">Response: {INTENT_LABEL[call.intent] || call.intent}</p>
                                                        )}
                                                        {call.error_message && (
                                                            <p className="text-xs text-red-500 dark:text-red-400 mt-2">Error: {call.error_message}</p>
                                                        )}

                                                        {open && (
                                                            <div className="mt-3 pt-3 border-t border-gray-100 dark:border-gray-700/50">
                                                                {detail?.loading ? (
                                                                    <div className="flex justify-center py-4"><div className="w-4 h-4 border-2 border-purple-400 border-t-transparent rounded-full animate-spin" /></div>
                                                                ) : !detail?.data?.found ? (
                                                                    <p className="text-xs text-gray-400 dark:text-gray-500 py-2">No call details yet — the call hasn't completed.</p>
                                                                ) : (
                                                                    <>
                                                                        <div className="inline-flex items-center gap-1 bg-gray-100 dark:bg-white/10 rounded-lg p-0.5 mb-3">
                                                                            {[["intent", "Intent"], ["transcript", "Transcript"]].map(([key, label]) => (
                                                                                <button
                                                                                    key={key}
                                                                                    onClick={() => setCallSubTab(prev => ({ ...prev, [call.id]: key }))}
                                                                                    className={`px-3 py-1 rounded-md text-xs font-medium transition-all ${
                                                                                        subTab === key ? "bg-white dark:bg-white/10 text-purple-600 dark:text-purple-300 shadow-sm" : "text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200"
                                                                                    }`}
                                                                                >
                                                                                    {label}
                                                                                </button>
                                                                            ))}
                                                                        </div>

                                                                        {subTab === "intent" ? (
                                                                            outcome ? (
                                                                                <div className="flex flex-col gap-2">
                                                                                    {interest && (
                                                                                        <span className={`inline-flex w-fit items-center px-2 py-0.5 rounded-full text-xs font-medium ${interest.className}`}>
                                                                                            {interest.label}
                                                                                        </span>
                                                                                    )}
                                                                                    {INTENT_FIELDS.map(([key, label]) => (
                                                                                        outcome[key] != null && (
                                                                                            <div key={key} className="flex gap-3 text-xs">
                                                                                                <span className="w-28 shrink-0 text-gray-400 dark:text-gray-500">{label}</span>
                                                                                                <span className="text-gray-700 dark:text-gray-300">{String(outcome[key])}</span>
                                                                                            </div>
                                                                                        )
                                                                                    ))}
                                                                                    {outcome.open_to_relocate != null && (
                                                                                        <div className="flex gap-3 text-xs">
                                                                                            <span className="w-28 shrink-0 text-gray-400 dark:text-gray-500">Open to relocate</span>
                                                                                            <span className="text-gray-700 dark:text-gray-300">{outcome.open_to_relocate ? "Yes" : "No"}</span>
                                                                                        </div>
                                                                                    )}
                                                                                    {outcome.summary && (
                                                                                        <p className="text-xs text-gray-600 dark:text-gray-300 mt-1 italic">&quot;{outcome.summary}&quot;</p>
                                                                                    )}
                                                                                </div>
                                                                            ) : (
                                                                                <p className="text-xs text-gray-400 dark:text-gray-500">No intent extracted yet.</p>
                                                                            )
                                                                        ) : (
                                                                            transcript.length > 0 ? (
                                                                                <div className="flex flex-col gap-2 max-h-64 overflow-y-auto pr-1">
                                                                                    {transcript.map((msg, i) => (
                                                                                        <div key={i} className={`flex ${msg.role === "user" ? "justify-end" : "justify-start"}`}>
                                                                                            <div
                                                                                                className={`max-w-[80%] rounded-lg px-3 py-1.5 text-xs ${
                                                                                                    msg.role === "user"
                                                                                                        ? "bg-purple-50 text-purple-900 dark:bg-purple-500/10 dark:text-purple-200"
                                                                                                        : "bg-gray-100 text-gray-700 dark:bg-white/[0.06] dark:text-gray-300"
                                                                                                }`}
                                                                                            >
                                                                                                {msg.text}
                                                                                            </div>
                                                                                        </div>
                                                                                    ))}
                                                                                </div>
                                                                            ) : (
                                                                                <p className="text-xs text-gray-400 dark:text-gray-500">No transcript available.</p>
                                                                            )
                                                                        )}
                                                                    </>
                                                                )}
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            )}
                        </div>
                    )
                )}
            </div>
        </div>
    );
};

export default CandidateDetailTabs;
